import { fabric } from 'fabric'
import { defined, equal, getProps, getStringExtension, isString, pipe } from 'junctions-fp'
import { v4 } from 'uuid'

import EventEmitter from 'events'
import HistoryManager from './HitoryManager'

import FontFaceObserver from 'fontfaceobserver'
import AddCommand from './command/AddCommand'
import ColorCommand from './command/ColorCommand'
import EffectCommand from './command/EffectCommand'
import FullSizeCommand from './command/FullSizeCommand'
import OpacityCommand from './command/OpacityCommand'
import PasteCommand from './command/PasteCommand'
import RemoveCommand from './command/RemoveCommand'
import RotateCommand from './command/RotateCommand'
import Alignement from './elements/Alignement'
import BackGround from './elements/BackGround'
import Grid from './elements/Grid'
import KeyBoardManager from './elements/KeyBoardManager'
import { calculateAspectRatioFit } from './tools/ImageSize'
import { getWorkSpaceSize } from './tools/WorksSpace'

import LoadTemplateCommand from './command/LoadTemplateCommand'

/**
 * Pixel Editor Class
 */
class PixelEditor extends EventEmitter {
  /**
   *
   * @param {string} containerID canvas element id
   */
  constructor(container, config) {
    super()
    const defaultConfig = { magnets: true, width: 880, height: 440, maxWidth: 2000, maxHeight: 2000 }
    const { width, height, magnets, maxWidth, maxHeight } = { ...defaultConfig, ...config }
    fabric.Object.NUM_FRACTION_DIGITS = 10
    fabric.textureSize = 6000
    fabric.filterBackend = fabric.initFilterBackend()
    this.guideMagnets = magnets
    this.width = width
    this.height = height
    this.background = null
    this.zoomfactor = 0.5
    this.initialDpr = this.getDevicePixelRatio()
    this.oldCenterPos = {
      x: 0,
      y: 0
    }
    this.oldWorkspace = {
      top: 0,
      left: 0
    }
    this.lineConfig = {
      stroke: '#F00',
      strokeWidth: 1,
      selectable: false,
      opacity: 0.3,
      visible: true,
      hoverCursor: 'default',
      evented: false
    }
    if (container) {
      this.setContainer(container, maxWidth, maxHeight)
    }
  }

  /**
   * Sets the container for the canvas and initializes the necessary Fabric.js objects.
   *
   * @param {HTMLElement} container - The HTML element that will contain the canvas.
   * @param {number} maxWidth - The maximum width of the canvas.
   * @param {number} maxHeight - The maximum height of the canvas.
   */
  setContainer(container, maxWidth, maxHeight) {
    this._initFabricObjects()
    this._createCanvas(container, maxWidth, maxHeight)
    this._createListeners()
    this._draw()
  }

  /**
   * initialize les propriété de base des objets de fabric
   * @private
   */
  _initFabricObjects() {
    fabric.Object.prototype.objectCaching = true
    fabric.Object.prototype.set({
      transparentCorners: false,
      cornerColor: 'rgba(0,0,0,0.5)',
      borderColor: '#555',
      cornerSize: 8,
      strokeWidth: 0,
      padding: 0,
      borderDashArray: [2, 2],
      hasControls: true,
      uid: 0,
      isLogo: false,
      layer: 0,
      locked: false
    })
  }

  /**
   * Creates and sets up the event listeners for the canvas.
   *
   * This function sets up various event listeners for the canvas, such as window resize, object modifications,
   * selection changes, mouse events, and keyboard events. Each event listener is bound to a specific function
   * to handle the event.
   */
  _createListeners() {
    window.addEventListener('resize', this._draw.bind(this))
    this.canvas.on('object:modified', this._objectModified.bind(this))
    this.canvas.on('selection:created', this._objectSelected.bind(this))
    this.canvas.on('mouse:up', this._objectSelected.bind(this))
    this.canvas.on('object:moving', this._moving.bind(this))
    this.canvas.on('text:changed', evt => this.emit('modified'))
    this.canvas.on('selection:cleared', evt => {
      if (evt.deselected) {
        evt.deselected.forEach(item => {
          if (item.type === 'textbox' && item.text.trim() === '') {
            this.canvas.remove(item)
            this.emit('removeItem', [item])
          } else if (item.type === 'textbox') {
            item.isEditing = false
          }
        })
      }
      this.emit('unselected', evt.deselected)
    })
    this.canvas.on(
      'mouse:wheel',
      evt => {
        const wheel = evt.e.wheelDelta / 120
        this.zoom(wheel / 10)
      },
      {
        passive: true
      }
    )

    this.KeyBoardManager = new KeyBoardManager(this.canvas)
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_COPY, this.copySelection.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_DELETE, this.deleteCurrent.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_GRID, this.showGrid.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_PASTE, this.pasteSelection.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_LOCK_SCALE_OFF, this.scaleProportionnalOff.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_LOCK_SCALE_ON, this.scaleProportionnalOn.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_UNDO, this.undo.bind(this))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_CREATE_TEXTBOX, event => this.emit(KeyBoardManager.EVENT_CREATE_TEXTBOX))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_FILTER, event => this.emit(KeyBoardManager.EVENT_SHOW_FILTER))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_IMAGE, event => this.emit(KeyBoardManager.EVENT_SHOW_IMAGE))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_ADJUSTEMENT, event => this.emit(KeyBoardManager.EVENT_SHOW_ADJUSTEMENT))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_SHAPE, event => this.emit(KeyBoardManager.EVENT_SHOW_SHAPE))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_TEMPLATE, event => this.emit(KeyBoardManager.EVENT_SHOW_TEMPLATE))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_ACTIVE_MAGNET, event => this.emit(KeyBoardManager.EVENT_ACTIVE_MAGNET))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_SHOW_LAYER, event => this.emit(KeyBoardManager.EVENT_SHOW_LAYER))
    this.KeyBoardManager.addListener(KeyBoardManager.EVENT_ARROW, this.movingArrow.bind(this))
  }

  /// ///////////////////////////////////////////////////////
  // CANVAS
  /// ///////////////////////////////////////////////////////
  /**
   * Creates a new canvas element and sets up the Fabric.js canvas.
   *
   * @param {HTMLElement} container - The HTML element that will contain the canvas.
   * @param {number} maxWidth - The maximum width of the canvas.
   * @param {number} maxHeight - The maximum height of the canvas.
   *
   */
  _createCanvas(container, maxWidth, maxHeight) {
    this.container = container
    this.canvasElement = document.createElement('canvas')
    this.container.appendChild(this.canvasElement)
    this.canvas = new fabric.Canvas(this.canvasElement, {
      preserveObjectStacking: true,
      backgroundColor: null,
      uniScaleTransform: true,
      selection: false,
      originX: 'center',
      originY: 'center'
    })
    this.canvas.controlsAboveOverlay = true
    this.canvas.unsiScaleKey = 'null'
    this.canvas.setWidth(maxWidth)
    this.canvas.setHeight(maxHeight)
    this.canvas.setZoom(this.zoomfactor)
    this.historyManager = new HistoryManager(this)
    this.emit('zoomUpadted', this.canvas.getZoom())
    this.historyManager = new HistoryManager(this)
    this.alignement = new Alignement(this)
    this.alignement.draw()
  }

  /**
   * Draws the background and grid on the canvas, and positions all objects on the canvas.
   */
  _draw() {
    // If the background object has not been created yet, create it
    if (!this.background) {
      this.background = new BackGround(this.canvas)
    }
    // Draw the background on the canvas
    this.background.draw({
      width: this.width,
      height: this.height
    })

    // If the grid object has not been created yet, create it
    if (!this.grid) {
      this.grid = new Grid(20, this.lineConfig)
    }
    // Draw the grid on the canvas
    this.grid.draw(this.canvas, {
      width: this.width,
      height: this.height
    })

    // Get the size of the workspace and set the clipTo property of the canvas to clip the drawing to the workspace size
    const ws = getWorkSpaceSize(this.width, this.height, this.canvas)
    this.canvas.clipTo = ctx => {
      ctx.rect(ws.x, ws.y, ws.width, ws.height)
    }

    // Position all objects on the canvas based on the current viewport center position
    this.__positionAll()
    // Request a render of the canvas to update the drawing
    this.canvas.requestRenderAll()
  }

  /**
   * Positions all objects on the canvas based on the current viewport center position.
   *
   * This function calculates the difference between the current viewport center position
   * and the previous viewport center position. It then applies this difference to the
   * position of each object on the canvas, and updates the object's coordinates.
   *
   * @private
   */
  __positionAll() {
    // Get the current viewport center position
    const newPosCenter = this.canvas.getVpCenter()
    // Calculate the difference between the current viewport center position and the previous viewport center position
    const diffCenter = {
      left: newPosCenter.x - this.oldCenterPos.x,
      top: newPosCenter.y - this.oldCenterPos.y
    }
    // Loop through all custom objects on the canvas
    this._getCustomObjects().forEach(item => {
      // Apply the difference to the position of the object
      item.set({
        left: item.left + diffCenter.left,
        top: item.top + diffCenter.top
      })
      // Update the object's coordinates
      item.setCoords()
    })
    // Update the previous viewport center position
    this.oldCenterPos = newPosCenter
  }

  /**
   * La taille de la zone d'edition
   * @param {number} width
   * @param {number} height
   */
  changeFormat(width, height) {
    if (width === this.width && height === this.heigh) {
      return
    }
    this.width = width
    this.height = height
    this._draw()
  }

  /**
   * Zooms the canvas in or out by the specified value.
   *
   * @param {number} value - The amount to zoom the canvas by. A positive value zooms in, and a negative value zooms out.
   * @param {boolean} [absolute=false] - Whether to set the zoom level to the specified value, or to adjust the current zoom level by the specified value.
   */
  zoom(value, absolute = false) {
    // If the absolute parameter is true, set the zoom level to the specified value
    if (absolute) {
      this.canvas.zoomToPoint(new fabric.Point(this.canvas.width / 2, this.canvas.height / 2), value)
      this._draw()
      return
    }

    // Calculate the new zoom level by adding the specified value to the current zoom level
    const zv = this.canvas.getZoom() + value
    // If the new zoom level is within the valid range, zoom the canvas to the new zoom level
    if (zv > 0.01 && zv < 5) {
      let center = this.canvas.getCenter()
      let point = {
        x: center.left,
        y: center.top
      }
      this.canvas.zoomToPoint(new fabric.Point(this.canvas.width / 2, this.canvas.height / 2), this.canvas.getZoom() + value)
      this._draw()
      this.emit('zoomUpadted', this.canvas.getZoom())
    }
  }

  /**
   * Returns the current zoom level of the canvas.
   *
   * @returns {number} The current zoom level of the canvas.
   */
  getZoom() {
    return this.canvas.getZoom()
  }

  /**
   * Returns the current size of the work area on the canvas.
   *
   * @returns {Object} An object containing the width and height of the work area.
   */
  getCurrentWorkSize() {
    return { width: this.width, height: this.height }
  }

  /**
   * Resets the canvas by clearing all objects and redrawing the canvas.
   */
  reset() {
    this.canvas.clear()
    this._drawCanvas()
  }

  /**
   * Handles the "object:moving" event on the canvas.
   *
   * If the guideMagnets property is true, snaps the object to the grid.
   *
   * @param {Event} evt - The "object:moving" event object.
   * @private
   */
  _moving(evt) {
    if (this.guideMagnets) {
      this.grid.snaptToGrid(evt)
    }
    this.emit('modified')
  }

  /**
   * Moves the currently selected objects on the canvas in the specified direction.
   *
   * @param {string} direction - The direction to move the objects in. Must be one of "arrowup", "arrowdown", or "arrowleft".
   * @param {boolean} shiftPressed - Whether the shift key is pressed. If true, moves the objects by a larger amount.
   */
  movingArrow(direction, shiftPressed) {
    if (!this.getCurrentSelectedObjects().length) {
      return
    }
    let value = 1 * (shiftPressed ? 10 : 1)
    let pos = { left: value, top: 0 }
    switch (direction) {
      case 'arrowup':
        pos = { left: 0, top: -value }
        break
      case 'arrowdown':
        pos = { left: 0, top: value }
        break
      case 'arrowleft':
        pos = { left: -value, top: 0 }
        break
    }

    const x = this.canvas._activeObject
    x.set({ top: x.top + pos.top, left: x.left + pos.left })
    x.setCoords()
    this.historyManager.forceSnap()
    this.canvas.requestRenderAll()
  }

  /**
   * Toggles the use of magnets on the canvas.
   *
   * If the guideMagnets property is true, magnets are enabled and objects will snap to the grid when moved.
   * If the guideMagnets property is false, magnets are disabled and objects will not snap to the grid when moved.
   */
  toggleMagnet() {
    this.guideMagnets = !this.guideMagnets
  }

  //////////////////////////////////////////////////////////
  // IMAGES
  //////////////////////////////////////////////////////////
  /**
   * Charge une image dans le canvas
   * @param {string|File} data url de l'image
   */
  loadImage(data, isLogo = false) {
    if (isString(data)) {
      if (getStringExtension(data) === '.svg') {
        fabric.loadSVGFromURL(data, this._onLoadSvg.bind(this, isLogo), null, { crossOrigin: 'anonymous' })
        return
      }
      fabric.util.loadImage(
        data,
        img => {
          if (img !== null) {
            const image = new fabric.Image(img, {
              crossOrigin: 'anonymous'
            })
            this._onLoadImg(isLogo, image)
          }
        },
        null,
        {
          crossOrigin: 'anonymous'
        }
      )
    } else {
      const reader = new FileReader()
      reader.addEventListener(
        'load',
        () => {
          if (this._getStringInfoImg(reader.result).contentType[1] === 'image/svg+xml') {
            fabric.loadSVGFromURL(reader.result, this._onLoadSvg.bind(this, isLogo), null, { crossOrigin: 'anonymous' })
          } else {
            fabric.Image.fromURL(reader.result, this._onLoadImg.bind(this, isLogo), {
              crossOrigin: 'anonymous'
            })
          }
        },
        false
      )
      reader.readAsDataURL(data)
    }
  }

  /**
   * appeler lorsque l'image est chargé
   * @param {image} oI image data
   * @private
   */
  _onLoadImg(isLogo, oI) {
    const img = this._configImage(oI, isLogo)
    this.addCommand(new AddCommand(this, img))
    this.emit('imageLoaded', img)
    this.emit('selectedObject', img)
  }

  /**
   * appeler lorsque l'image est chargé
   * @param {image} oI image data
   * @private
   */
  _onLoadSvg(isLogo, objects, options) {
    const obj = fabric.util.groupSVGElements(objects, options)
    obj.subtype = 'SVG'
    obj.isLogo = isLogo
    this.addCommand(new AddCommand(this, obj))
    this.emit('imageLoaded', obj)
  }

  /**
   *
   * @param {image} img image data
   * @returns {image} image data configured
   * @private
   */
  _configImage(img, isLogo = false) {
    const size = calculateAspectRatioFit(img.width, img.height, this.width, this.height)
    img.scaleToWidth(size.width)
    img.scaleToHeight(size.height)
    img.isLogo = isLogo
    return img
  }

  /// ///////////////////////////////////////////////////////
  // FONT
  /// ///////////////////////////////////////////////////////
  /**
   *
   * @param {*} opts
   */
  insertText(opts) {
    const text = new fabric.Textbox('', opts)
    this.addCommand(new AddCommand(this, text))
    text.enterEditing()
  }

  /**
   * Modifie la police
   * @param {*} opts
   */
  async updateTextBox(opts) {
    const activeObject = this.canvas.getActiveObject()
    if (activeObject) {
      if (opts.fontFamily) {
        const myfont = new FontFaceObserver(opts.fontFamily)
        await myfont.load()
      }
      if (activeObject.selectionEnd - activeObject.selectionStart !== 0) {
        activeObject.setSelectionStyles(opts, activeObject.selectionStart, activeObject.selectionEnd)
      } else {
        activeObject.set(opts)
      }
      this.canvas.requestRenderAll()
    }
  }

  addFilter(filter, filterIndex, save = false) {
    const f = new EffectCommand(this, filter, filterIndex, save ? this.canvas.getActiveObject().filters[filterIndex] : null)
    if (save) {
      this.addCommand(f)
      return
    }
    f.execute()
    //
  }

  addShadow(options) {
    const selected = this.canvas.getActiveObject()
    if (selected) {
      this.canvas.getActiveObject()
      selected.setShadow(options)
      this.canvas.requestRenderAll()
    }
  }

  applyModification() {
    this._objectModified()
  }

  /**
   *
   *
   * @returns {string}
   * @memberof PixelEditor
   */

  export() {
    return this.canvas.toDatalessJSON(['uid', 'isLogo', 'subtype', 'layer', 'locked'])
  }
  /**
   *
   *
   * @param {any} data
   * @memberof PixelEditor
   */
  loadTemplate(data) {
    this.addCommand(new LoadTemplateCommand(this, data))
  }
  ////////////////////////////////////////////////
  // ADD | REMOVE | SELECT
  ///////////////////////////////////////////////
  /**
   * ajoute un objet au canvas
   *
   * @param {any} command
   * @memberof PixelEditor
   */
  addCommand(command) {
    this.historyManager.add(command)
    this.historyManager.execute()
    this.canvas.requestRenderAll()
  }
  /**
   * supprime l'objet courant selectionné
   *
   * @memberof PixelEditor
   */
  deleteCurrent() {
    this.addCommand(new RemoveCommand(this, this.canvas.getActiveObjects()))
    this.historyManager.execute()
  }

  /**
   * selectionne l'objet grace a son id unique
   *
   * @param {any} uid
   * @memberof PixelEditor
   */
  selectByUID(uid) {
    const currentObject = this._getObjectByUID(uid)
    if (!currentObject.locked) {
      this.canvas.setActiveObject(currentObject)
      this.canvas.requestRenderAll()
    }
  }

  /**
   * supprime un objet grace a son id unique
   *
   * @param {any} uid
   * @memberof PixelEditor
   */
  deleteByUID(uid) {
    let object = this._getObjectByUID(uid)
    this.addCommand(new RemoveCommand(this, object))
    this.canvas.remove(object)
  }
  /**
   * supprime un objet grace a son id unique
   *
   * @param {any} uid
   * @memberof PixelEditor
   */
  lockLayer(uid) {
    const currentObject = this._getObjectByUID(uid)
    const l = !currentObject.locked
    currentObject.selectable = !l
    currentObject.evented = !l
    currentObject.locked = l
    currentObject.editable = currentObject.selectable
    this.canvas.discardActiveObject()
    this.canvas.requestRenderAll()
    this.emit('modified')
  }

  /**
   * Récupère un objet grace a son id unique
   *
   * @param {int} uid l'id unique de l'objet
   * @returns {Object}
   * @memberof PixelEditor
   */
  _getObjectByUID(uid) {
    const list = this.canvas.getObjects()
    return list.find(object => defined(object.uid) && object.uid === uid)
  }

  scaleProportionnalOn() {
    if (defined(this.canvas.getActiveObject())) {
      if (this.canvas.getActiveObject().type === 'textbox' && this.canvas.getActiveObject().isEditing) {
        return
      }
      this.canvas.getActiveObject().lockUniScaling = true
    }
  }

  scaleProportionnalOff() {
    if (defined(this.canvas.getActiveObject())) {
      this.canvas.getActiveObject().lockUniScaling = false
    }
  }

  pasteSelection() {
    if (defined(this.canvas.getActiveObject())) {
      const pc = new PasteCommand(this)
      this.addCommand(pc)
    }
  }

  copySelection() {
    if (defined(this.canvas.getActiveObject())) {
      this.canvas.getActiveObject().clone(cloned => (this.clipBoard = cloned))
    }
  }

  showGrid() {
    this.grid.toggle()
    this.canvas.requestRenderAll()
  }

  /**
   *
   * @param {*} shape
   * @param {*} color
   */
  insertShape(shape, color = '#000') {
    let s = null
    switch (shape) {
      case 'square':
        s = new fabric.Rect({
          width: 200,
          height: 200,
          fill: color
        })
        break
      case 'rectangle':
        s = new fabric.Rect({
          width: 300,
          height: 150,
          fill: color
        })
        break
      case 'circle':
        s = new fabric.Circle({
          radius: 100,
          fill: color
        })
        break
      case 'triangle':
        s = new fabric.Triangle({
          width: 200,
          height: 200,
          fill: color
        })
        break
    }
    this.addCommand(new AddCommand(this, s))
  }

  undo() {
    const command = this.historyManager.undo()
    this.canvas.requestRenderAll()
    this.emit('updateHistory', {
      history: this.historyManager.history,
      command
    })
  }

  get history() {
    return this.historyManager
  }

  _objectSelected(e) {
    this.alignement.clear()
    this.canvas.isDragging = false
    this.canvas.selection = true
    if (this.canvas.getActiveObject() != null) {
      this.canvas.getActiveObject().clone(elem => {
        this.selectedObject = elem
      })
      this.emit('selectedObject', this.canvas.getActiveObject())
    }
  }

  getCurrentSelectedObject() {
    return defined(this.canvas) ? this.canvas.getActiveObject() : null
  }

  getCurrentSelectedObjects() {
    return defined(this.canvas) ? this.canvas.getActiveObjects() : null
  }
  /**
   * met l'élément a la position z index definit +1
   * (le background est toujours le z index 0)
   *
   * @param {any} uid
   * @param {any} newIndex
   * @memberof PixelEditor
   */
  swapLayers(uid, newIndex) {
    const nindex = newIndex + 1
    const element = this._getObjectByUID(uid)
    element.layer = nindex
    element.moveTo(nindex)
    this.grid.updateLayer(this)
  }

  ////////////////////////////////////////////////
  // TRANSFORMATION
  ///////////////////////////////////////////////
  _objectModified(e) {
    this.emit('modified')
    this.historyManager.forceSnap()
    this.selectedObject = this.canvas.getActiveObject()
  }

  /**
   *
   * @param {*} value
   */
  setColor(value) {
    const active = this.canvas.getActiveObject()
    if (active) {
      this.addCommand(new ColorCommand(active, value))
    }
  }

  rotate(angle = 90) {
    if (defined(this.canvas.getActiveObject())) {
      this.addCommand(new RotateCommand(this, angle))
    }
  }

  fullSize(direction) {
    if (defined(this.canvas.getActiveObject())) {
      this.addCommand(new FullSizeCommand(this, direction))
    }
  }

  setOpacity(value) {
    const active = this.canvas.getActiveObject()
    if (active) {
      active.set({
        opacity: value
      })
      this.canvas.requestRenderAll()
    }
  }

  saveOpacity(newValue, oldValue) {
    this.addCommand(new OpacityCommand(this, newValue, oldValue))
  }

  getOpacity() {
    const active = this.canvas.getActiveObject()
    if (active) {
      return active.get('opacity')
    }
    return null
  }

  /**
   * calcule le dpi du device
   */
  getDevicePixelRatio() {
    let mediaQuery
    if (window.devicePixelRatio !== undefined) {
      return window.devicePixelRatio
    } else if (window.matchMedia) {
      mediaQuery =
        '(-webkit-min-device-pixel-ratio: 1.5),\
          (min--moz-device-pixel-ratio: 1.5),\
          (-o-min-device-pixel-ratio: 3/2),\
          (min-resolution: 1.5dppx)'
      if (window.matchMedia(mediaQuery).matches) {
        return 1.5
      }
      mediaQuery =
        '(-webkit-min-device-pixel-ratio: 2),\
          (min--moz-device-pixel-ratio: 2),\
          (-o-min-device-pixel-ratio: 2/1),\
          (min-resolution: 2dppx)'
      if (window.matchMedia(mediaQuery).matches) {
        return 2
      }
      mediaQuery =
        '(-webkit-min-device-pixel-ratio: 0.75),\
          (min--moz-device-pixel-ratio: 0.75),\
          (-o-min-device-pixel-ratio: 3/4),\
          (min-resolution: 0.75dppx)'
      if (window.matchMedia(mediaQuery).matches) {
        return 0.7
      }
      return 1
    } else {
      return 1
    }
  }

  /**
   * Perme la sauvegarde d'un fichier image
   * @param {string} type le type de fichier a enregistrer
   * @param {number} quality la qualité d'image de 0 a 1 jpeg uniquement
   */
  savePicture(type = 'png', quality = 1) {
    // recupère le pixel ratio
    let dpr = this.getDevicePixelRatio()

    // recupère l'etat de la grille
    const showGrid = this.grid.visible
    // récupère l'etat du zomm
    const currentZoom = this.canvas.getZoom()
    // force la grille a se masquer
    this.grid.hide()
    // force le background a se masquer
    this.background.hide()

    // force le zoom a 100%
    this.zoom(1, true)
    // deselectionne tout les objets
    this.canvas.discardActiveObject()
    // desactive le skipoffscreen
    this.canvas.skipOffscreen = false
    // effectue un rendu global du canvas
    this.canvas.renderAll()

    // creéation du canvas d'export
    const tmpCanvas = document.createElement('canvas')
    //const currentDPR = this.getDevicePixelRatio()

    // configure la taille du canvas
    tmpCanvas.width = this.width
    tmpCanvas.height = this.height

    // récupération du context
    const context = tmpCanvas.getContext('2d')
    // recupération des position  x y du mask
    const x = (this.canvas.getWidth() - this.width) / 2
    const y = (this.canvas.getHeight() - this.height) / 2

    // creation de l'image a partir des coordonée et du dpr
    context.drawImage(
      this.canvasElement,
      x * this.initialDpr,
      y * this.initialDpr,
      this.width * this.initialDpr,
      this.height * this.initialDpr,
      0,
      0,
      this.width,
      this.height
    )
    // recuperation de l'image en base64
    const img = tmpCanvas.toDataURL('image/' + type, quality)

    // on remet le zoom a son état initial
    this.zoom(currentZoom, true)

    // on remet le background a son etat initial
    this.background.show()

    // si la grille etait visible on l'affiche a nouveau
    if (showGrid) {
      this.grid.show()
    }
    // on refait un rendu
    this.canvas.renderAll()

    // on retourne l'imgage base64
    return img
  }
  /**
   * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
   * images to fit into a certain area.
   *
   * @param {Number} srcWidth width of source image
   * @param {Number} srcHeight height of source image
   * @param {Number} maxWidth maximum available width
   * @param {Number} maxHeight maximum available height
   * @return {Object} { width, height }
   */
  calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight)

    return { width: srcWidth * ratio, height: srcHeight * ratio }
  }

  /**
   * Creation d'une image pour les previews
   */
  templateThumb() {
    const contentType = 'image/jpeg'
    // recupère le pixel ratio
    let dpr = Math.abs(this.getDevicePixelRatio())
    // recupère l'etat de la grille
    const showGrid = this.grid.visible
    // récupère l'etat du zomm
    const currentZoom = this.canvas.getZoom()
    // force la grille a se masquer
    this.grid.hide()
    // force le background a se masquer
    this.background.hide()

    // force le zoom a 100%
    this.zoom(1, true)
    // deselectionne tout les objets
    this.canvas.discardActiveObject()
    // desactive le skipoffscreen
    this.canvas.skipOffscreen = false
    // effectue un rendu global du canvas
    this.canvas.renderAll()

    // creéation du canvas d'export
    const tmpCanvas = document.createElement('canvas')

    // récupération du context
    const context = tmpCanvas.getContext('2d')
    // recupération des position  x y du mask
    const x = (this.canvas.getWidth() - this.width) / 2
    const y = (this.canvas.getHeight() - this.height) / 2

    const size = calculateAspectRatioFit(this.width * dpr, this.height * dpr, 56, 56)
    // configure la taille du canvas
    tmpCanvas.width = size.width
    tmpCanvas.height = size.height

    // creation de l'image a partir des coordonée et du dpr
    context.drawImage(this.canvasElement, x * dpr, y * dpr, this.width * dpr, this.height * dpr, 0, 0, size.width, size.height)
    // recuperation de l'image en base64
    const img = tmpCanvas.toDataURL(contentType, 1)

    // on remet le zoom a son état initial
    this.zoom(currentZoom, true)

    // on remet le background a son etat initial
    this.background.show()

    // si la grille etait visible on l'affiche a nouveau
    if (showGrid) {
      this.grid.show()
    }
    // on refait un rendu
    this.canvas.renderAll()
    // on retourne l'imgage base64
    const block = img.split(';')
    const ct = block[0].split(':')
    const data = block[1].split(',')[1]
    const blob = this.b64toBlob(data, ct)
    return {
      uid: `${v4()}_preview`,
      blob
    }
  }

  _getCustomObjects() {
    return defined(this.canvas) ? this.canvas.getObjects().filter(o => o.uid !== 0) : null
  }

  get logo() {
    return this.canvas.getObjects() && this._getCustomObjects() ? this.canvas.getObjects().find(o => o.isLogo) : null
  }

  selectLogo() {
    const logo = this.logo
    if (logo) {
      this.canvas.setActiveObject(logo)
      this.canvas.requestRenderAll()
    }
  }

  _updateB64Src(arrayUrls) {
    arrayUrls.forEach(item => {
      this._getObjectByUID(item.uid).set('src', item.url)
    })
  }

  getImagesCount() {
    const isImage = pipe(getProps('type'), equal('image'))
    return this._getCustomObjects().filter(isImage).length
  }

  /**
   * Récupère la liste des images importer par l'utilisateur en base 64 afin de les uploader
   *
   * @returns {Array} un tableau d'images base 64
   */
  _getBase64Images() {
    const substringBase64 = value => value.substring(0, 4) === 'data'
    const isImage = pipe(getProps('type'), equal('image'))
    const getSrc = pipe(getProps('_originalElement'), getProps('src'))
    const isBase64 = pipe(getSrc, substringBase64, equal(true))
    const allValid = item => isImage(item) === true && isBase64(item) === true
    return this._getCustomObjects() ? this._getCustomObjects().filter(allValid) : []
  }

  _convertAllImg64() {
    const getSrc = pipe(getProps('_originalElement'), getProps('src'))
    return this._getBase64Images().map(item => {
      const block = getSrc(item).split(';')
      const contentType = block[0].split(':')
      const data = block[1].split(',')[1]
      const blob = this.b64toBlob(data, contentType)
      return {
        uid: item.uid,
        blob
      }
    })
  }

  _getStringInfoImg(str) {
    const block = str.split(';')
    const header = block[0]
    const contentType = header.split(':')
    const data = block[1].split(',')[1]
    return {
      contentType,
      data
    }
  }

  /**
   * Convert a base64 string in a Blob according to the data and contentType.
   *
   * @param b64Data {String} Pure base64 string without contentType
   * @param contentType {String} the content type of the file i.e (image/jpeg - image/png - text/plain)
   * @param sliceSize {Int} SliceSize to process the byteCharacters
   * @see http://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
   * @return Blob
   */
  b64toBlob(b64Data, contentType, sliceSize) {
    contentType = contentType || ''
    sliceSize = sliceSize || 512

    const byteCharacters = atob(b64Data)
    let byteArrays = []

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      let byteNumbers = new Array(slice.length)
      for (var i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      let byteArray = new Uint8Array(byteNumbers)

      byteArrays.push(byteArray)
    }
    return new Blob(byteArrays, {
      type: contentType
    })
  }
}

export default PixelEditor



// WEBPACK FOOTER //
// ./src/model/pixel/PixelEditor.js